import type { DashboardSettingsResponseDto } from '@api'
import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { timezoneOptions } from './consts'

/*
 * Gets a relative date based on an input like we store in fanInfo
 * @param {string} dateString
 * @returns
 */
export function getRelativeDate(sinceDate: string) {
  if (!sinceDate) return

  const date = new Date(sinceDate).getTime()
  const currentDate = new Date().getTime()
  const diff = currentDate - date

  const seconds = Math.floor(diff / 1000)

  if (seconds < 60) {
    return seconds + (seconds > 1 ? ' seconds ago' : ' second ago')
  }

  const minutes = Math.floor(seconds / 60)

  if (minutes < 60) {
    return minutes + (minutes > 1 ? ' minutes ago' : ' minute ago')
  }

  const hours = Math.floor(minutes / 60)

  if (hours < 24) {
    return hours + (hours > 1 ? ' hours ago' : ' hour ago')
  }

  const days = Math.floor(hours / 24)

  if (days < 30) {
    return days + (days > 1 ? ' days ago' : ' day ago')
  }

  const months = Math.floor(days / 30)

  if (months < 12) {
    return months + (months > 1 ? ' months ago' : ' month ago')
  }

  const years = Math.floor(months / 12)

  return years + (years > 1 ? ' years ago' : ' year ago')
}

export function toUsdFormat(amount: number, showDecimals = false) {
  let usdFormat

  if (showDecimals) {
    usdFormat = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })
  } else {
    usdFormat = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    })
  }

  return usdFormat.format(amount)
}

export function getCurrentTimeInTimezone(timezone?: string | null) {
  if (!timezone || timezone === 'Undefined') {
    return 'Not set'
  }

  const selectedOption = resolveTimezoneOption(timezone)

  if (!selectedOption) {
    return 'Not set'
  }

  const value = selectedOption.value
  const now = new Date()
  const zonedDate = toZonedTime(now, value)
  const formattedTime = format(zonedDate, 'hh:mm a')

  return `${formattedTime}`
}

// option contains zone offset at the end that could be changed with switch to DST.
// So try to compare the part of the timezone label without timezone offset.
export function resolveTimezoneOption(timezone?: string | null) {
  return timezoneOptions.find(
    (option) =>
      option.label.slice(0, -timezoneOffsetLength) ===
      timezone?.slice(0, -timezoneOffsetLength),
  )
}

// the part after GMT is added separately, and we have a difference there in case of DST applied
const timezoneOffsetLength = 6

export function unescapeHtmlText(htmlStr?: string) {
  if (!htmlStr) {
    return ''
  }

  htmlStr = htmlStr.replace(/&lt;/g, '<')
  htmlStr = htmlStr.replace(/&gt;/g, '>')
  htmlStr = htmlStr.replace(/&quot;/g, '"')
  htmlStr = htmlStr.replace(/&#39;/g, "'")
  htmlStr = htmlStr.replace(/&amp;/g, '&')

  return htmlStr
}

const spacePattern = /[\s]/g

function removeSpaces(text: string) {
  return text.replace(spacePattern, '')
}

export function isPresetInserted(
  notice: string,
  preset?: DashboardSettingsResponseDto['customNotePreset'],
) {
  if (!preset) return false

  return removeSpaces(notice).includes(removeSpaces(preset))
}
